import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { UserContact } from '../types/user.type';
import { ADMIN_USERS_API_PATHS } from '../constants/users-paths.constants';

type UseAdminGetUserContactsResult = {
  isLoading: boolean;
  contacts: UserContact[];
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetUserContactsResult, 'refetch'> = {
  isLoading: true,
  contacts: [],
};

export function useAdminGetUserContacts({ userId }: { userId: string }): UseAdminGetUserContactsResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const refetch = useCallback(async (): Promise<void> => {
    try {
      const response = await HttpClient.get<UserContact[]>(ADMIN_USERS_API_PATHS.GET_USER_CONTACTS(userId));

      setResult({ isLoading: false, contacts: response });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, 'Une erreur est survenue lors de la récupération des contacts'),
        { variant: 'error' }
      );
      setResult({ isLoading: false, contacts: [] });
    }
  }, [userId, enqueueSnackbar]);

  useEffect(() => {
    setResult(INITIAL_STATE);
  }, [userId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
